import React, { useState, useEffect } from 'react';
import { fetchHealthSystemCases } from '../../services/api';
import Pagination from '../shared/Pagination';
import InPersonAudioRecorder from './InPersonAudioRecorder';

/**
 * AnkrCases - Display provider's cases from Ankr system
 * Fetches and displays cases using the provider cases API endpoint
 */
const AnkrCases = ({ providerInfo, authData, isAuthenticated, updateUrlWithTab, onTabChange }) => {
  const [cases, setCases] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [caseView, setCaseView] = useState('self'); // 'self' for provider cases, 'all' for health system cases
  const [filters, setFilters] = useState({
    status: '',
    dateFrom: '',
    dateTo: '',
    providerId: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  
  // In-person recording state - check URL on mount
  const [showInPersonRecorder, setShowInPersonRecorder] = useState(() => {
    // Check if URL indicates we should show in-person recorder
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('mode') === 'in-person';
  });
  const [selectedCaseForRecording, setSelectedCaseForRecording] = useState(() => {
    // Check if we have a specific case ID in the URL
    const urlParams = new URLSearchParams(window.location.search);
    const caseId = urlParams.get('id');
    const mode = urlParams.get('mode');
    if (mode === 'in-person' && caseId) {
      // We'll load the case data once cases are fetched
      return { id: caseId, loading: true };
    }
    return null;
  });
  
  // Token authentication state
  const [apiToken, setApiToken] = useState('');
  const [authLoading, setAuthLoading] = useState(false);
  const [authStatus, setAuthStatus] = useState(null);

  // Auto-dismiss status messages after 5 seconds
  useEffect(() => {
    if (authStatus) {
      const timer = setTimeout(() => {
        setAuthStatus(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [authStatus]);

  // Handle token authentication
  const handleAuthenticate = async () => {
    if (!apiToken || apiToken.trim() === '') {
      setAuthStatus({ type: 'error', message: 'Please enter an API token' });
      return;
    }

    setAuthLoading(true);
    setAuthStatus(null);

    // Send validation request via postMessage (similar to IframeTester)
    window.postMessage({
      type: 'VALIDATE_TOKEN_REQUEST',
      payload: {
        api_token: apiToken,
        request_id: Date.now()
      }
    }, window.location.origin);
  };

  // Listen for token validation responses
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.origin !== window.location.origin) {
        return;
      }
      const { type, payload } = event.data;
      
      if (type === 'TOKEN_VALIDATION_RESPONSE') {
        if (payload.success) {
          // Only reload for manual authentication, not automatic loading from storage
          if (!payload.from_storage) {
            setAuthStatus({ 
              type: 'success', 
              message: '✅ Token authenticated successfully. Reloading page...' 
            });
            // Reload the page after a brief delay to show the success message
            setTimeout(() => {
              window.location.reload();
            }, 1500);
          }
        } else {
          setAuthStatus({ 
            type: 'error', 
            message: `❌ ${payload.error || 'Token validation failed'}` 
          });
        }
        setAuthLoading(false);
      }
    };
    
    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  // Fetch cases when component mounts or auth data changes
  useEffect(() => {
    const loadCases = async () => {
      if (!isAuthenticated || !authData?.api_token || !providerInfo?.provider_id) {
        console.log('Debug:', { isAuthenticated, hasToken: !!authData?.api_token, hasProviderId: !!providerInfo?.provider_id });
        return;
      }
      setLoading(true);
      setError(null);
      try {
        // Use the healthcare endpoint with scope parameter for both views
        const scope = caseView === 'self' ? 'self' : 'all';
        const response = await fetchHealthSystemCases(authData.api_token, currentPage, 20, searchTerm, {...filters, scope});
        setCases(response?.cases || []);
        setPagination(response?.pagination || null);
      } catch (err) {
        console.error('Failed to fetch cases:', err);
        setError(err.message || 'Failed to load cases');
      } finally {
        setLoading(false);
      }
    };
    loadCases();
  }, [isAuthenticated, authData?.api_token, providerInfo?.provider_id, currentPage, searchTerm, caseView, filters.status, filters.dateFrom, filters.dateTo, filters.providerId]);

  // Load selected case data from URL when cases are available
  useEffect(() => {
    if (selectedCaseForRecording?.loading && cases.length > 0) {
      const caseId = selectedCaseForRecording.id;
      const foundCase = cases.find(c => c.id === caseId);
      
      if (foundCase) {
        // Transform the case data for the recorder
        const transformedCase = {
          ...foundCase,
          case_id: foundCase.id,
          patient: foundCase.patient_info_fk ? {
            first_name: foundCase.patient_info_fk.first_name,
            last_name: foundCase.patient_info_fk.last_name,
            dob: foundCase.patient_info_fk.dob,
            patient_id: foundCase.patient_info_fk.patient_id || foundCase.patient_info_fk.id,
            id: foundCase.patient_info_fk.id,
            phone: foundCase.patient_info_fk.phone,
            email: foundCase.patient_info_fk.email,
            ehr_medical_record_number: foundCase.patient_info_fk.ehr_medical_record_number
          } : null
        };
        
        setSelectedCaseForRecording(transformedCase);
        
        // Notify parent component about the selected case
        if (updateUrlWithTab) {
          updateUrlWithTab('cases', foundCase.id);
        }
      } else {
        console.warn('Case ID from URL not found in fetched cases:', caseId);
        // Reset to null if case not found
        setSelectedCaseForRecording(null);
      }
    }
  }, [cases, selectedCaseForRecording]);

  // Handle refresh
  const handleRefresh = () => {
    if (isAuthenticated && authData?.api_token && providerInfo?.provider_id) {
      const loadCases = async () => {
        setLoading(true);
        setError(null);

        try {
          // Use the healthcare endpoint with scope parameter for both views
          const scope = caseView === 'self' ? 'self' : 'all';
          const response = await fetchHealthSystemCases(authData.api_token, currentPage, 20, searchTerm, {...filters, scope});
          setCases(response?.cases || []);
          setPagination(response?.pagination || null);
        } catch (err) {
          console.error('Failed to refresh cases:', err);
          setError(err.message || 'Failed to load cases');
        } finally {
          setLoading(false);
        }
      };

      loadCases();
    }
  };

  // Handle case view toggle
  const handleCaseViewChange = (newView) => {
    setCaseView(newView);
    setCurrentPage(1); // Reset to first page when switching views
    // Keep searchTerm and filters when switching views
  };

  // Handle filter changes
  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
    setCurrentPage(1); // Reset to first page when filtering
  };

  // Clear all filters
  const clearAllFilters = () => {
    setFilters({
      status: '',
      dateFrom: '',
      dateTo: '',
      providerId: ''
    });
    setSearchTerm('');
    setCurrentPage(1);
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (err) {
      return dateString;
    }
  };

  // Get status badge class
  const getStatusBadgeClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'initiated':
      case 'short_url_sent':
      case 'short_url_accessed':
      case 'yob_validated':
      case 'token_generated':
        return 'ankr-status-badge info';
      case 'active':
      case 'in_progress':
      case 'patient_checkin_initiated':
      case 'device_check_complete':
      case 'forms_started':
      case 'forms_filled':
      case 'forms_completed':
      case 'insurance_started':
      case 'insurance_verified':
        return 'ankr-status-badge active';
      case 'patient_declined_video':
      case 'patient_requested_human_support':
      case 'locked - attempts exceeded':
        return 'ankr-status-badge warning';
      case 'completed':
      case 'check_in_complete':
      case 'unlocked_by_admin':
        return 'ankr-status-badge completed';
      case 'cancelled':
      case 'expired':
        return 'ankr-status-badge cancelled';
      default:
        return 'ankr-status-badge default';
    }
  };

  // Get patient display name
  const getPatientName = (patientInfo) => {
    if (!patientInfo) return 'Unknown Patient';
    const firstName = patientInfo.first_name || '';
    const lastName = patientInfo.last_name || '';
    return `${firstName} ${lastName}`.trim() || patientInfo.patient_id || 'Unknown Patient';
  };

  // Handle phone call
  const handlePhoneCall = (phoneNumber) => {
    if (phoneNumber) {
      window.open(`tel:${phoneNumber}`);
    }
  };

  // Handle SMS
  const handleSMS = (phoneNumber) => {
    if (phoneNumber) {
      window.open(`sms:${phoneNumber}`);
    }
  };

  // Handle email
  const handleEmail = (email) => {
    if (email) {
      window.open(`mailto:${email}`);
    }
  };

  // Handle video visit (similar to IframeTester.js)
  const handleVideoVisit = (event, caseItem) => {
    // Prevent event bubbling and default behavior
    event.preventDefault();
    event.stopPropagation();
    
    if (!caseItem.latest_zoom_meeting || !caseItem.latest_zoom_meeting.topic) {
      alert('Patient declined video visit or video session not available');
      return;
    }

    console.log('Starting video visit for case:', caseItem.id);
    
    // Update URL with case ID
    if (updateUrlWithTab) {
      updateUrlWithTab('cases', caseItem.id);
    }

    const payload = {
      api_token: authData?.api_token,
      mirasol_case_id: caseItem.id,
      provider_info: {
        id: providerInfo?.provider_id,
        name: providerInfo?.name || 'Unknown Provider',
        specialty: providerInfo?.specialty || ''
      },
      health_system_info: {
        id: caseItem.healthcare_info_fk?.health_system_id || caseItem.healthcare_info_fk?.id,
        name: caseItem.healthcare_info_fk?.name || 'Unknown Health System'
      },
      zoom_session: {
        topic: caseItem.latest_zoom_meeting.topic,
        passcode: caseItem.latest_zoom_meeting.passcode,
        attendeeName: providerInfo?.name || 'Provider',
        role: '1' // Host (Provider)
      }
    };

    console.log('Sending video visit message:', payload);

    // Send message to join Zoom session (using same type as App.js message handler)
    window.postMessage({
      type: 'initiateVideoVisit',
      payload: {
        zoom_session_id: caseItem.id,
        latest_zoom_meeting: caseItem.latest_zoom_meeting,
        miraSolCaseId: caseItem.id,
        providerInfo: {
          id: providerInfo?.provider_id,
          name: providerInfo?.name || 'Unknown Provider',
          specialty: providerInfo?.specialty || ''
        },
        healthcareInfo: {
          id: caseItem.healthcare_info_fk?.health_system_id || caseItem.healthcare_info_fk?.id,
          name: caseItem.healthcare_info_fk?.name || 'Unknown Health System'
        }
      }
    }, window.location.origin);
  };

  // Handle phone visit - navigate to Notes page
  const handlePhoneVisit = (caseItem) => {
    console.log('Navigating to Notes page for case:', caseItem.id);
    
    // Update URL to go to Notes tab with the case ID
    if (updateUrlWithTab) {
      updateUrlWithTab('notes', caseItem.id);
    }
    
    // Change the active tab to notes
    if (onTabChange) {
      onTabChange('notes');
    }
  };

  // Handle starting in-person visit
  const handleStartInPersonVisit = (caseId = null) => {
    // Update URL to reflect in-person mode
    const url = new URL(window.location);
    url.searchParams.set('mode', 'in-person');
    
    if (caseId) {
      // Store selected case info for the recorder
      const selectedCase = cases.find(c => c.id === caseId);
      if (selectedCase) {
        // Add case ID to URL
        url.searchParams.set('id', caseId);
        
        // Update parent component state with selected case ID
        if (updateUrlWithTab) {
          updateUrlWithTab('cases', caseId);
        }
        
        // Transform the case data to match what InPersonAudioRecorder expects
        const transformedCase = {
          ...selectedCase,
          case_id: selectedCase.id,
          // Map patient_info_fk to patient for compatibility with InPersonAudioRecorder
          patient: selectedCase.patient_info_fk ? {
            first_name: selectedCase.patient_info_fk.first_name,
            last_name: selectedCase.patient_info_fk.last_name,
            dob: selectedCase.patient_info_fk.dob,
            patient_id: selectedCase.patient_info_fk.patient_id || selectedCase.patient_info_fk.id,
            id: selectedCase.patient_info_fk.id,
            phone: selectedCase.patient_info_fk.phone,
            email: selectedCase.patient_info_fk.email,
            ehr_medical_record_number: selectedCase.patient_info_fk.ehr_medical_record_number
          } : null
        };
        
        setSelectedCaseForRecording(transformedCase);
      }
    } else {
      // No specific case selected - remove case ID from URL
      url.searchParams.delete('id');
      setSelectedCaseForRecording(null);
    }
    
    // Update browser URL
    window.history.pushState({}, '', url);
    setShowInPersonRecorder(true);
  };

  // Handle closing in-person recorder
  const handleCloseInPersonRecorder = () => {
    // Clean up URL parameters
    const url = new URL(window.location);
    url.searchParams.delete('mode');
    url.searchParams.delete('id');
    window.history.pushState({}, '', url);
    
    setShowInPersonRecorder(false);
    setSelectedCaseForRecording(null);
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= (pagination?.total_pages || 1)) {
      setCurrentPage(newPage);
    }
  };

  // Handle search with debouncing
  const handleSearchChange = (value) => {
    setSearchTerm(value);
    setCurrentPage(1); // Reset to first page on search
    // Clear existing timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    // Set new timeout for debounced search
    const newTimeout = setTimeout(() => {
      // The useEffect will trigger automatically due to searchTerm change
    }, 300);
    setSearchTimeout(newTimeout);
  };
  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTimeout]);

  if (!isAuthenticated) {
    return (
      <div className="ankr-cases-container">
        <div className="ankr-cases-content">
          {/* Status Messages */}
          {authStatus && (
            <div className={`ankr-auth-status ${authStatus.type}`}>
              {authStatus.message}
            </div>
          )}
          <div className="empty-state">          
          <div className="ankr-auth-icon">🔐</div>
          <p>Authentication Required</p>
          <small>Please sign in to view and manage Ankr cases.</small>
          
          {/* Token Input Section - Hidden by default, can be shown with a toggle */}
          <div className="ankr-auth-form">
            <div className="ankr-form-group">
              {/* <label htmlFor="access-token">Access Token:</label> */}
              <input
                id="access-token"
                type="text"
                className="ankr-token-input"
                value={apiToken}
                onChange={(e) => setApiToken(e.target.value)}
                placeholder=""
                disabled={authLoading}
              />
            </div>
            <button
              className="ankr-login-button"
              onClick={handleAuthenticate}
              disabled={authLoading || !apiToken.trim()}
            >
              {authLoading ? 'Authenticating...' : 'Login'}
            </button>
          </div>
        </div>
        </div>
      </div>
    );
  }

  if (!providerInfo?.provider_id) {
    return (
      <div className="ankr-cases-container">
        <div className="ankr-no-provider">
          <div className="ankr-provider-icon">👤</div>
          <h3>Provider Information Missing</h3>
          <p>Unable to load cases without provider information.</p>
        </div>
      </div>
    );
  }

  // Show in-person recorder if active
  if (showInPersonRecorder) {
    return (
      <div className="ankr-cases-container">
        <InPersonAudioRecorder
          providerInfo={providerInfo}
          authData={authData}
          selectedCase={selectedCaseForRecording}
          onClose={handleCloseInPersonRecorder}
          enableRealtime={true}
        />
      </div>
    );
  }

  return (
    <div className="ankr-cases-container">
      {/* Header */}
      <div className="ankr-cases-header">
        <div className="ankr-header-left">
          <h3>Ankr Cases</h3>
          <p>{caseView === 'self' ? 'Cases assigned to you' : 'All cases'}</p>
        </div>
        <div className="d-flex d-flex-inline gap-2">
          <button
            className="btn btn-warning"
            onClick={handleStartInPersonVisit}
            title="Start new in-person visit"
          >
            <i className="fas fa-user-plus d-none d-md-inline"></i>
            <span className="d-md-none d-xl-inline"><i className="fas fa-plus me-2"></i>In-Person Visit</span>
          </button>
          <button className="scroll-button" onClick={handleRefresh} disabled={loading} title="Refresh cases" >
            {loading ? '⟳' : '↻'}
          </button>
        </div>
      </div>

      {/* Case View Toggle */}
      <div className="category-tabs px-3">
        <div className="btn-group" role="group" aria-label="Case view toggle">
          <button
            type="button"
            className={`category-tab ${caseView === 'self' ? 'active' : ''}`}
            onClick={() => handleCaseViewChange('self')}
          ><span style={{ fontSize: '16px', paddingRight: '10px' }}>👨‍⚕️</span>My Cases
          </button>
          <button
            type="button"
            className={`category-tab ${caseView === 'all' ? 'active' : ''}`}
            onClick={() => handleCaseViewChange('all')}
          ><span style={{ fontSize: '16px', paddingRight: '10px' }}>🏥</span>All Cases
          </button>
        </div>
      </div>

      {/* Search Box */}
      <div className="ankr-search-container">
        <div className="d-flex gap-2 align-items-center">
          <div className="flex-grow-1 position-relative">
            <input
              type="text"
              className="ankr-search-input"
              placeholder={caseView === 'self' 
                ? "Search your cases by patient name, MRN, case ID, or status..." 
                : "Search all cases by patient name, MRN, case ID, provider, or status..."
              }
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
            />
            {searchTerm && (
              <button
                className="ankr-search-clear"
                onClick={() => handleSearchChange('')}
                title="Clear search"
              >
                ×
              </button>
            )}
          </div>
          <button
              className={`btn btn-sm ${showFilters ? 'btn-secondary' : 'btn-outline-secondary'}`}
              onClick={() => setShowFilters(!showFilters)}
              title={showFilters ? 'Hide filters' : 'Show filters'}
            >
              <i className="fas fa-filter"></i> Filters
            </button>
        </div>
      </div>

      {/* Filters - Show when filters toggle is active */}
      {showFilters && (
        <div className="ankr-filters-container p-3">
          <div className="row g-2">
            <div className="col-md-3">
              <select
                className="form-select form-select-sm"
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <option value="">All Statuses</option>
                <option value="initiated">Initiated</option>
                <option value="active">Active</option>
                <option value="in_progress">In Progress</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
                <option value="expired">Expired</option>
              </select>
            </div>
            <div className="col-md-3">
              {/* <label className="form-label small text-muted mb-1">Created From:</label> */}
              <input
                type="date"
                className="form-control form-control-sm"
                value={filters.dateFrom}
                onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                title="Filter cases created from this date"
              />
            </div>
            <div className="col-md-3">
              {/* <label className="form-label small text-muted mb-1">Created To:</label> */}
              <input
                type="date"
                className="form-control form-control-sm"
                value={filters.dateTo}
                onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                title="Filter cases created up to this date"
              />
            </div>
            <div className="col-md-3">
              <div className="d-flex gap-1">
                <button
                  className="btn btn-outline-secondary btn-sm flex-grow-1"
                  onClick={clearAllFilters}
                  title="Clear all filters"
                >
                  <i className="fas fa-filter-circle-xmark"></i>
                </button>
              </div>
            </div>
          </div>
          {(filters.status || filters.dateFrom || filters.dateTo) && (
            <div className="active-filters mt-2">
              <small className="text-muted">
                <i className="fas fa-filter"></i> Active filters: 
                {filters.status && <span className="badge bg-primary ms-1">Status: {filters.status}</span>}
                {filters.dateFrom && <span className="badge bg-primary ms-1">Created From: {filters.dateFrom}</span>}
                {filters.dateTo && <span className="badge bg-primary ms-1">Created To: {filters.dateTo}</span>}
              </small>
            </div>
          )}
        </div>
      )}
      {/* Content */}
      <div className="ankr-cases-content">
        {loading && (
          <div className="ankr-loading-state">
            <div className="ankr-loading-spinner"></div>
            <p>Loading cases...</p>
          </div>
        )}

        {error && (
          <div className="error-state">
            <div className="ankr-error-icon">⚠️</div>
            <p style={{marginBottom:0}}>Unable to load cases</p>
            <p className="small mb-3">{error}</p>
            <button className="button secondary" onClick={handleRefresh}>
              Try Again
            </button>
          </div>
        )}

        {!loading && !error && cases.length === 0 && !searchTerm && (
          <div className="empty-state">
            <div className="ankr-empty-icon">📋</div>
            <h4>No Cases Found</h4>
            <p>You don't have any cases yet, or they may not be visible with your current permissions.</p>
          </div>
        )}

        {!loading && !error && cases.length === 0 && searchTerm && (
          <div className="ankr-empty-state">
            <div className="ankr-empty-icon">🔍</div>
            <h4>No Matching Cases</h4>
            <p>No cases match your search "{searchTerm}". Try a different search term.</p>
            <button 
              className="ankr-retry-button" 
              onClick={() => handleSearchChange('')}
            >
              Clear Search
            </button>
          </div>
        )}

        {!loading && !error && cases.length > 0 && (
          <div className="ankr-cases-list">
            {searchTerm && pagination && (
              <div className="ankr-search-results">
                Found {pagination.total_count} cases matching "{searchTerm}"
              </div>
            )}
            {cases.map(caseItem => (
              <div key={caseItem.id} className="ankr-case-item">
                <div className="ankr-case-header">
                  <div className="ankr-case-id">
                    Case #{caseItem.CoreServer_CaseID || caseItem.id}
                  </div>
                  <div className={getStatusBadgeClass(caseItem.mira_sol_case_status)}>
                    {caseItem.mira_sol_case_status?.replace(/_/g, ' ') || 'Unknown'}
                  </div>
                </div>

                <div className="ankr-case-details">
                  {/* Patient Name */}
                  <div className="d-flex d-flex-inline">
                    <strong>{getPatientName(caseItem.patient_info_fk)}</strong>
                  </div>

                  {/* DOB & MRN */}
                  <div className="d-flex d-flex-inline small text-muted">
                    {caseItem.patient_info_fk?.dob && (
                      <><b>DOB:&nbsp;</b> {new Date(caseItem.patient_info_fk.dob).toLocaleDateString()} | &nbsp;</>
                    )}
                   <b>MRN:&nbsp;</b> {caseItem.patient_info_fk?.ehr_medical_record_number || 'Not found'}
                  </div>

                  {/* Contact Info */}
                  {(caseItem.patient_info_fk?.phone || caseItem.patient_info_fk?.email) && (
                    <div className="ankr-detail-row text-lowercase" style={{ fontSize: '0.9em' }}>
                      {caseItem.patient_info_fk?.phone && (
                        <span style={{ marginRight: '0.5rem' }}>
                          {caseItem.patient_info_fk.phone}
                          <button className="ankr-icon-button" onClick={() => handlePhoneCall(caseItem.patient_info_fk.phone)} title="Call">📞</button>
                          <button className="ankr-icon-button" onClick={() => handleSMS(caseItem.patient_info_fk.phone)} title="SMS">💬</button>
                        </span>
                      )}
                      {caseItem.patient_info_fk?.email && (
                        <span>
                          {caseItem.patient_info_fk.email}
                          <button className="ankr-icon-button" onClick={() => handleEmail(caseItem.patient_info_fk.email)} title="Email">📧</button>
                        </span>
                      )}
                    </div>
                  )}

                  {/* Created Date & Patient URL */}
                  <div className="d-flex d-flex-inline small text-muted">
                    <b>Created:&nbsp;</b> {formatDate(caseItem.created_at)}</div>
                  {caseItem.short_url && (
                    <div className="d-flex d-flex-inline small text-muted">
                      <b>Patient invite link:&nbsp;</b> <a href={caseItem.short_url} target="_blank" rel="noopener noreferrer">{caseItem.short_url}</a>
                    </div>  
                    )}

                  {/* Visit Buttons */}
                  <div style={{ marginTop: '0.5rem' }}>
                    <div className="d-flex gap-2" style={{ flexWrap: 'wrap' }}>
                      <button
                        className={`btn btn-sm btn-primary ${!caseItem.latest_zoom_meeting?.topic ? 'disabled' : ''}`}
                        onClick={(event) => handleVideoVisit(event, caseItem)}
                        disabled={!caseItem.latest_zoom_meeting?.topic}
                        title={caseItem.latest_zoom_meeting?.topic ? "Start video visit" : "Patient declined video visit"}
                      >
                        <i className="fas fa-video"></i> {caseItem.latest_zoom_meeting?.topic ? 'Video' : 'No Video'}
                      </button>
                      <button
                        className="btn btn-sm btn-success"
                        onClick={() => handlePhoneVisit(caseItem)}
                        title="Start phone visit"
                      >
                        <i className="fas fa-phone"></i> Phone
                      </button>
                      <button
                        className="btn btn-sm btn-warning"
                        onClick={() => handleStartInPersonVisit(caseItem.id)}
                        title="Start in-person visit with audio recording"
                      >
                        <i className="fas fa-user-plus"></i> In-Person
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination Controls */}
        {!loading && !error && cases.length > 0 && (
          <Pagination
          pagination={pagination}
          onPageChange={handlePageChange}
          loading={loading}
          className="ankr-pagination"
          />)
        }
      </div>
    </div>
  );
};

export default AnkrCases;